import MiawClient, {
	genId,
} from './MiawClient.js';
import MiawEventStream from './MiawEventStream.js';
import {
	MIAW_CONFIG,
} from './config.js';

// Orchestrates a MIAW conversation for the UI: owns the client + event stream +
// an in-memory message list, optimistically shows the user's own messages and
// dedupes the echoed copy, and exposes a thin subscribe/start/send/end surface so
// the React panel stays a thin view. Framework-agnostic and unit-testable (inject
// a client + streamFactory); a bronco-proxy transport can be swapped underneath
// without touching the panel.
export default class MiawSession {
	constructor(options = {}) {
		this.client = options.client || new MiawClient(options.config || MIAW_CONFIG);
		this.streamFactory = options.streamFactory || ((client, conversationId, handlers) => {
			return new MiawEventStream(client, conversationId, handlers);
		});
		this.conversationId = options.conversationId || null;
		this.messages = [];
		this.status = 'idle'; // idle | connecting | open | reconnecting | closed | error
		this.typing = false;  // agent is composing a reply
		this.stalled = false; // sent a message but nothing came back within the watchdog window
		this.stream = null;
		this.listeners = new Set();
		this.pendingIds = new Set(); // client messageIds shown optimistically, awaiting echo
		// If the conversation is routed away from the bot (e.g. escalation to a rep
		// queue with nobody staffed), no reply — and no error — ever arrives. The
		// watchdog turns that silence into a visible stalled state instead of
		// leaving the typing dots up forever.
		this.replyTimeoutMs = options.replyTimeoutMs || 30000;
		this.replyWatchdog = null;
	}

	subscribe(fn) {
		this.listeners.add(fn);
		return () => {
			return this.listeners.delete(fn);
		};
	}

	emit() {
		const snapshot = {
			messages: this.messages.slice(),
			status: this.status,
			typing: this.typing,
			stalled: this.stalled,
		};
		this.listeners.forEach((fn) => {
			return fn(snapshot);
		});
	}

	armReplyWatchdog() {
		this.clearReplyWatchdog();
		this.replyWatchdog = setTimeout(() => {
			this.replyWatchdog = null;
			this.typing = false;
			this.stalled = true;
			this.emit();
		}, this.replyTimeoutMs);
	}

	clearReplyWatchdog() {
		if (this.replyWatchdog) {
			clearTimeout(this.replyWatchdog);
			this.replyWatchdog = null;
		}
	}

	setStatus(status) {
		this.status = status;
		this.emit();
	}

	streamHandlers() {
		return {
			onStatus: (state) => {
				return this.setStatus(state);
			},
			onMessage: (payload) => {
				return this.ingestMessage(payload);
			},
			onTyping: (isStarted) => {
				this.typing = !!isStarted;
				// The other side is alive (composing) — give it a fresh window. Also
				// re-arm after a stall: typing hides the stalled notice, so without a
				// live watchdog a rep who starts typing and walks away would bring the
				// infinite dots back.
				if (isStarted && (this.replyWatchdog || this.stalled)) {
					this.armReplyWatchdog();
				}
				this.emit();
			},
		};
	}

	// Normalize a server CONVERSATION_MESSAGE / transcript entry into our shape.
	// MIAW's exact payload nesting is validated at e2e against the real deployment;
	// read defensively across the shapes the docs/sample show.
	normalize(payload) {
		const entry = (payload && (payload.conversationEntry || payload.entry)) || payload || {};

		// entryPayload is a stringified JSON blob over SSE (an object over REST
		// /entries). Parse it if needed; the message text lives at
		// entryPayload.abstractMessage.staticContent.text.
		let inner = entry.entryPayload;
		if (typeof inner === 'string') {
			try {
				inner = JSON.parse(inner);
			} catch (err) {
				inner = {};
			}
		}
		inner = inner || {};

		const staticContent = (inner.abstractMessage && inner.abstractMessage.staticContent)
			|| entry.staticContent
			|| {};
		const id = entry.identifier || entry.messageId || entry.id
			|| (inner.abstractMessage && inner.abstractMessage.id) || null;
		// Rich payloads (choices/escalation formats) can carry a non-string here;
		// coerce to '' so they're dropped instead of reaching React as an object child.
		const rawText = staticContent.text || entry.text || '';
		const text = typeof rawText === 'string' ? rawText : '';
		const role = (entry.sender && entry.sender.role) || entry.actorType || entry.senderRole || 'Agent';
		const isUser = role === 'EndUser' || role === 'user' || role === 'EndUserGuest';
		const timestamp = entry.transcriptedTimestamp || entry.timestamp || entry.clientTimestamp || Date.now();
		return {
			id,
			text,
			from: isUser ? 'user' : 'agent',
			timestamp,
		};
	}

	ingestMessage(payload) {
		const msg = this.normalize(payload);

		// Our own message echoed back — we already showed it optimistically.
		if (msg.id && this.pendingIds.has(msg.id)) {
			this.pendingIds.delete(msg.id);
			return;
		}
		// Already have it (e.g. replayed after a reconnect from Last-Event-Id).
		if (msg.id && this.messages.some((m) => m.id === msg.id)) {
			return;
		}
		if (!msg.text) {
			return;
		}

		// Append in arrival order — do NOT sort by timestamp. Optimistic user
		// messages carry local Date.now() while server entries carry server time,
		// so any browser/server clock skew re-sorts questions away from their
		// answers (QA: questions clumped in one block). SSE delivers in server
		// order and sends are appended when made, so arrival order is already the
		// conversational order. resume() still sorts — its entries all share the
		// server clock.
		this.messages.push(msg);
		// An inbound (agent) message means the reply has landed — stop the
		// indicator and stand down the watchdog.
		if (msg.from === 'agent') {
			this.typing = false;
			this.stalled = false;
			this.clearReplyWatchdog();
		}
		this.emit();
	}

	attachStream() {
		this.stream = this.streamFactory(this.client, this.conversationId, this.streamHandlers());
		this.stream.start();
	}

	// Start a NEW conversation. routingAttributes = identity + page context.
	async start(routingAttributes = {}) {
		this.setStatus('connecting');
		try {
			this.conversationId = await this.client.createConversation(routingAttributes);
			this.attachStream();
			return this.conversationId;
		} catch (err) {
			console.error('MIAW start failed:', err);
			this.setStatus('error');
			throw err;
		}
	}

	// Resume an existing conversation (page reload): rehydrate transcript, re-attach.
	async resume(conversationId) {
		this.conversationId = conversationId;
		this.setStatus('connecting');
		try {
			// History is the JSON entries list. (/transcript is a PDF download, not
			// message history — don't use it here.)
			const history = await this.client.listEntries(conversationId);
			const entries = (history && (history.conversationEntries || history.entries)) || [];
			this.messages = entries
				.map((entry) => this.normalize(entry))
				.filter((m) => m.text);
			this.messages.sort((a, b) => a.timestamp - b.timestamp);
			this.emit();
			this.attachStream();
		} catch (err) {
			console.error('MIAW resume failed:', err);
			this.setStatus('error');
			throw err;
		}
	}

	async send(text) {
		if (!this.conversationId) {
			throw new Error('MIAW send() called before start()/resume()');
		}
		const messageId = genId();
		this.pendingIds.add(messageId);
		this.messages.push({
			id: messageId,
			text,
			from: 'user',
			timestamp: Date.now(),
		});
		// Show a "waiting for a reply" indicator immediately; the agent's
		// TYPING_STARTED/STOPPED events (or its message) update it from here.
		this.typing = true;
		this.stalled = false;
		this.armReplyWatchdog();
		this.emit();

		try {
			await this.client.sendMessage(this.conversationId, text, messageId);
		} catch (err) {
			console.error('MIAW send failed:', err);
			this.typing = false;
			this.clearReplyWatchdog();
			const pending = this.messages.find((m) => m.id === messageId);
			if (pending) {
				pending.failed = true;
			}
			this.emit();
			throw err;
		}
		return messageId;
	}

	// Detach the stream WITHOUT ending the server-side conversation (navigation /
	// unmount) — the conversation resumes on the next open.
	detach() {
		this.clearReplyWatchdog();
		if (this.stream) {
			this.stream.stop();
			this.stream = null;
		}
	}

	// Explicit "end chat" / logout — tears down the server-side conversation too.
	async end() {
		this.detach();
		if (this.conversationId) {
			try {
				await this.client.endSession(this.conversationId);
			} catch (err) {
				// best-effort
			}
		}
		this.conversationId = null;
		this.setStatus('closed');
	}
}
